/**
 * MoreSheet — "더보기" 하단 시트
 * - BottomNav "더보기" 탭에서 열림
 * - 프로필, 북마크, 피드, 크레딧, 설정, 요금제, 도움말, 테마, 언어
 * - BottomSheet.tsx와 동일한 Radix Dialog + Framer Motion 패턴
 */
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { bottomSheetVariants, fadeIn, useSnapVariants } from '../../lib/motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  User,
  Bookmark,
  Rss,
  Coins,
  Settings,
  CreditCard,
  HelpCircle,
  ShieldCheck,
  X,
} from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { useAuthStore } from '../../store/authStore';

interface MoreSheetProps {
  open: boolean;
  onClose: () => void;
}

interface MenuItem {
  icon: React.ElementType;
  label: string;
  path: string;
  requiresAuth?: boolean;
  requiresAdmin?: boolean;
}

const MENU_ITEMS: MenuItem[] = [
  { icon: User, label: '프로필', path: '/profile', requiresAuth: true },
  { icon: Bookmark, label: '북마크', path: '/bookmarks', requiresAuth: true },
  { icon: Rss, label: '피드', path: '/feed', requiresAuth: true },
  { icon: Coins, label: '크레딧', path: '/credits', requiresAuth: true },
  { icon: Settings, label: '설정', path: '/settings', requiresAuth: true },
  { icon: CreditCard, label: '요금제', path: '/pricing' },
  { icon: HelpCircle, label: '도움말', path: '/help' },
  { icon: ShieldCheck, label: '관리자', path: '/admin', requiresAuth: true, requiresAdmin: true },
];

const MoreSheet: React.FC<MoreSheetProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuthStore();
  const snapOverlay = useSnapVariants(fadeIn);
  const snapSheet = useSnapVariants(bottomSheetVariants);

  const isAdmin = user?.role === 'admin';
  const visibleItems = MENU_ITEMS.filter((item) => {
    if (item.requiresAuth && !isAuthenticated) return false;
    if (item.requiresAdmin && !isAdmin) return false;
    return true;
  });

  return (
    <Dialog.Root open={open} onOpenChange={(v) => !v && onClose()}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                className="fixed inset-0 z-[60] bg-black/50"
                {...snapOverlay}
              />
            </Dialog.Overlay>

            <Dialog.Content
              asChild
              onInteractOutside={onClose}
              onEscapeKeyDown={onClose}
            >
              <motion.div
                className="fixed bottom-0 left-0 right-0 z-[61] bg-white dark:bg-slate-900 rounded-t-2xl shadow-2xl max-h-[70vh] landscape-md:max-h-[90vh] md:max-h-[85vh] overflow-y-auto"
                style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
                {...snapSheet}
              >
                {/* 핸들 바 */}
                <div className="flex justify-center pt-3 pb-1">
                  <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-slate-600" />
                </div>

                {/* 헤더 */}
                <div className="flex items-center justify-between px-5 pb-3">
                  <Dialog.Title className="text-lg font-bold text-gray-800 dark:text-slate-100">
                    더보기
                  </Dialog.Title>
                  <Dialog.Close asChild>
                    <button
                      className="flex items-center justify-center w-9 h-9 rounded-full hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                      aria-label="닫기"
                    >
                      <X className="h-5 w-5 text-gray-500 dark:text-slate-400" />
                    </button>
                  </Dialog.Close>
                </div>

                {/* 메뉴 항목 */}
                <div className="px-4 pb-4">
                  {!isAuthenticated && (
                    <Link
                      to="/login"
                      onClick={onClose}
                      className="flex items-center gap-3 px-3 py-3 rounded-xl text-white bg-primary-600 hover:bg-primary-700 transition-colors mb-3 min-h-[44px]"
                    >
                      <User className="w-5 h-5 shrink-0" />
                      <span className="text-sm font-medium">로그인 / 회원가입</span>
                    </Link>
                  )}

                  <div className="grid grid-cols-3 gap-2">
                    {visibleItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={onClose}
                        className="flex flex-col items-center gap-1.5 px-2 py-3 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors min-h-[44px]"
                      >
                        <span className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300">
                          <item.icon className="w-5 h-5" />
                        </span>
                        <span className="text-xs text-gray-700 dark:text-slate-300 font-medium">
                          {item.label}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
};

export default MoreSheet;
